// Benchmarks tab: suites → benchmarks (tasks + leaderboard) → task / run detail.
//
// One container (#benchmarks) re-rendered per navigation. The selection lives in
// state.bench {bench, run, task} and mirrors into the URL query (?bench=…&run=…
// &task=…) so every drill-in is a shareable, reload-survivable link.
//
// While the worker is mining/generating, the benchmark page polls every 4s.
// Background refreshes NEVER blank the page: the last-good render stays up,
// scroll position is preserved across repaints, and a fetch failure shows a
// "reconnecting…" pill and keeps retrying instead of dying on "Loading…".
import { state, $, esc, get, usd, num, dayOf } from './core'
import { syncHash } from './router'

function patch(url: string, body: any) {
  return fetch(url, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then(function (r) { return r.json(); });
}
function post(url: string, body?: any) {
  return fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body || {}) }).then(function (r) { return r.json(); });
}

// ---- router integration -----------------------------------------------------

// Top-level sub-view of the tab: '' = flat benchmarks grid, 'suites' = grouped.
var benchSeg = '';

// The list's team facet ('' = all teams). A browse FILTER over allTeams —
// every viewer may pick any team (same rule as the Sessions team filter);
// it narrows a list everyone already sees, so it is deliberately NOT the
// dashboard's gated state.scope. URL-mirrored like benchSeg so a filtered
// list is a shareable, reload-survivable link.
var benchTeam = '';

export function getBenchParams(): Record<string, string> {
  var q: Record<string, string> = {};
  if (state.bench.bench) q.bench = state.bench.bench;
  if (state.bench.run) q.run = state.bench.run;
  if (state.bench.task) q.task = state.bench.task;
  if (benchSeg && !state.bench.bench) q.sub = benchSeg;
  if (benchTeam && !state.bench.bench) q.team = benchTeam;
  return q;
}

export function applyBenchParams(query: Record<string, string>): void {
  state.bench = { bench: query.bench || '', run: query.run || '', task: query.task || '' };
  benchSeg = query.sub === 'suites' ? 'suites' : '';
  benchTeam = query.team || '';
  renderBenchmarks();
}

function nav(sel: { bench?: string; run?: string; task?: string }): void {
  state.bench = { bench: sel.bench || '', run: sel.run || '', task: sel.task || '' };
  syncHash();
  renderBenchmarks();
}

// ---- shared bits ------------------------------------------------------------

var DIFF_ORDER = ['easy', 'medium', 'hard', 'xl'];
var DIFF_LABEL: Record<string, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard', xl: 'XL' };

var CHECK_TIP: Record<string, string> = {
  OK: 'Leak check passed: the prompt describes the symptom without giving away the fix.',
  LEAK: 'The generated prompt gives away HOW the fix works (an internal file, helper, or the approach itself) — an agent could shortcut straight to the answer, inflating its score. Fix: Regenerate, or edit the prompt by hand.',
  WRONG: 'The prompt contradicts what the held-out tests actually verify — usually a tooling/refactor PR with no user-visible bug, so no fair question exists. Fix: usually drop this task.',
  UNSOLVABLE: 'The fix’s essence is a new public name/spec the reporter could not have known, and the prompt doesn’t state it as the requested behavior — no agent could guess it. Fix: edit the prompt to state the desired API, or drop the task.',
};

function plural(n: number, word: string): string {
  return num(n) + ' ' + word + (n === 1 ? '' : 's');
}

function diffBadge(d: string | null): string {
  if (!d) return '<span class="badge b-null">—</span>';
  return '<span class="badge bench-diff-' + esc(d) + '">' + esc(DIFF_LABEL[d] || d) + '</span>';
}

function statusBadge(s: string): string {
  var cls = s === 'validated' ? 'b-success' : s === 'candidate' ? 'b-partial' : s === 'dropped' ? 'b-failure' : 'b-unknown';
  var tip = s === 'candidate' ? 'Mined and filtered; execution validation (the F2P/P2P sweep) has not run yet.' :
    s === 'validated' ? 'Execution-validated: the fail-to-pass flip was confirmed in the environment image.' :
      s === 'dropped' ? 'Excluded from scoring — NOT deleted. The task page records why; set Status back to candidate to retry after fixing the cause.' : '';
  return '<span class="badge ' + cls + '" title="' + esc(tip) + '">' + esc(s) + '</span>';
}

/** Provenance + leak-check verdict for the tasks table / task page. */
function promptBadge(t: any): string {
  if (t.promptSource === 'real_issue') return '<span class="badge b-success" title="The agent is prompted with the PR’s linked issue, verbatim — written by a real user before the fix existed.">real issue</span>';
  if (t.promptSource === 'edited') return '<span class="badge b-unknown" title="Edited by hand — the leak-check verdict no longer applies.">edited</span>';
  if (t.promptSource === 'generated') {
    var v = t.checkStatus || 'UNCHECKED';
    var cls = v === 'OK' ? 'b-success' : v === 'WRONG' || v === 'UNSOLVABLE' ? 'b-partial' : v === 'LEAK' ? 'b-failure' : 'b-unknown';
    // A passing leak check is the normal case — the badge just says
    // "generated" (tooltip carries the verdict); only failures get loud labels.
    return '<span class="badge ' + cls + '" title="' + esc(CHECK_TIP[v] || 'Generated; leak check pending.') + '">' + (v === 'OK' ? 'generated' : esc(v)) + '</span>';
  }
  return '<span class="badge b-null" title="No task prompt yet — run Generate task prompts.">—</span>';
}

function verdictBadge(v: string | null): string {
  if (!v) return '<span class="badge b-null">—</span>';
  var cls = v === 'resolved' ? 'b-success' : v === 'unresolved' ? 'b-failure' : 'b-partial';
  return '<span class="badge ' + cls + '">' + esc(v) + '</span>';
}

function pct(x: number | null): string {
  return x == null ? '—' : Math.round(x * 100) + '%';
}

/** Attempt wall-clock time: seconds under a minute, one decimal minute under
 *  ten, whole minutes above — so "5m" rows become "4.7m" / "6.8m" and read
 *  distinctly instead of all rounding to the same number. */
function fmtDuration(secs: number | null | undefined): string {
  if (secs == null) return '—';
  if (secs < 60) return Math.round(secs) + 's';
  var m = secs / 60;
  return (m < 10 ? m.toFixed(1) : String(Math.round(m))) + 'm';
}

function rateBar(x: number | null): string {
  if (x == null) return '—';
  return '<span class="bench-rate"><span class="bench-rate-num">' + pct(x) + '</span>' +
    '<span class="bar-track"><span class="bar-fill" style="width:' + Math.round(x * 100) + '%"></span></span></span>';
}

/** Inline back arrow for a page's title row — costs no vertical space. */
function backArrow(tip: string, sel: { bench?: string }): string {
  return '<a class="bench-back" href="javascript:void(0)" data-nav=\'' + esc(JSON.stringify(sel)) + '\' title="' + esc(tip) + '">&larr;</a>';
}

function wireNavLinks(root: HTMLElement): void {
  Array.prototype.forEach.call(root.querySelectorAll('[data-nav]'), function (el: HTMLElement) {
    el.onclick = function () { nav(JSON.parse(el.getAttribute('data-nav') || '{}')); };
  });
}

/** Save-button feedback: flash "Saved ✓" then hide (PM #12). */
function flashSaved(el: HTMLElement | null, then?: () => void): void {
  if (!el) { if (then) then(); return; }
  el.textContent = 'Saved ✓';
  setTimeout(function () { if (then) then(); }, 500);
}

// ---- suites list ------------------------------------------------------------

export function renderBenchmarks(): void {
  var root = $('#benchmarks');
  if (!root) return;
  if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
  if (state.bench.run) return renderRun(root, state.bench.run);
  if (state.bench.task) return renderTask(root, state.bench.task);
  if (state.bench.bench) return renderBenchmark(root, state.bench.bench, false);
  renderHome(root);
}

function newBenchForm(suites: any[], teams: any[], seg: string): string {
  var d = new Date(); var to = d.toISOString().slice(0, 10);
  d.setMonth(d.getMonth() - 6); var from = d.toISOString().slice(0, 10);
  return '<section class="panel"><div class="panel-head"><h2>Benchmarks</h2>' + seg +
    '<button class="btn" id="benchNewBtn">New benchmark</button></div>' +
    '<div id="benchNewForm" style="display:none">' +
    '<div class="bench-form-row">' +
    '<label>Repo <input id="nbRepo" placeholder="owner/name" class="bench-input"></label>' +
    '<label>Name <input id="nbName" placeholder="(auto)" class="bench-input"></label>' +
    // The owning team is a REQUIRED pick, and the list is EVERY team (allTeams
    // semantics — building a benchmark for another team's repo is the normal
    // case), so no default is preselected: an accidental first-option team on a
    // required field would be silently wrong attribution.
    '<label>For team <select id="nbTeam" class="bench-input">' +
    '<option value="">(pick a team)</option>' +
    teams.map(function (t) { return '<option value="' + esc(t.id) + '">' + esc(t.name) + '</option>'; }).join('') +
    '</select></label>' +
    '<label>PRs from <input id="nbFrom" type="date" value="' + from + '" class="bench-input"></label>' +
    '<label>to <input id="nbTo" type="date" value="' + to + '" class="bench-input"></label>' +
    (suites.length ? '<label>Suite <select id="nbSuite" class="bench-input">' +
      '<option value="">(none)</option>' +
      suites.map(function (s) { return '<option value="' + esc(s.id) + '">' + esc(s.name) + '</option>'; }).join('') +
      '</select></label>' : '') +
    '</div>' +
    '<div class="bench-form-row" style="margin-top:10px">' +
    '<label>Environment image <input id="nbImage" placeholder="registry/ci-image:tag (optional)" class="bench-input" style="min-width:260px"></label>' +
    '<label>Test command <input id="nbTest" placeholder="python -m pytest {files} --junitxml={out} (optional)" class="bench-input" style="min-width:300px"></label>' +
    '<label>Rebuild command <input id="nbRebuild" placeholder="pip install -e . (optional)" class="bench-input" style="min-width:200px"></label>' +
    '<button class="btn" id="nbGo">Mine PRs</button>' +
    '<button class="btn" id="nbCancel">Cancel</button>' +
    '</div>' +
    '<div class="bench-prompt-meta" style="margin-top:8px">Mining lists the repo’s merged PRs through the GitHub API (no clone) and keeps changes that touch both source and tests (3–1500 lines, docs/CI excluded). ' +
    'Access uses your org’s GitHub App. The image and commands are your CI setup — validation and runs execute in them; all can be set later on the benchmark page.</div>' +
    '<div id="nbErr" class="bench-prompt-meta" style="color:#b4452f"></div>' +
    '</div></section>';
}

function wireNewBenchForm(root: HTMLElement): void {
  var btn = document.getElementById('benchNewBtn');
  var form = document.getElementById('benchNewForm');
  var toggle = function (show: boolean) { if (form) form.style.display = show ? '' : 'none'; };
  if (btn && form) btn.onclick = function () { toggle(form!.style.display === 'none'); };
  var cancel = document.getElementById('nbCancel');
  if (cancel) cancel.onclick = function () { toggle(false); };
  var go = document.getElementById('nbGo');
  if (go) go.onclick = function () {
    var val = function (id: string) { var el = document.getElementById(id) as HTMLInputElement | null; return el ? el.value.trim() : ''; };
    var err = document.getElementById('nbErr');
    // Required pick — catch it here so the user gets told at the form, not via
    // a 400 round-trip (the server still enforces it regardless).
    if (!val('nbTeam')) { if (err) err.textContent = 'pick the team this benchmark is for'; return; }
    post('/api/bench/mine', {
      repo: val('nbRepo'), name: val('nbName') || undefined,
      teamId: val('nbTeam'),
      windowFrom: val('nbFrom') || undefined, windowTo: val('nbTo') || undefined,
      suiteId: val('nbSuite') || undefined,
      imageRef: val('nbImage') || undefined, testCmd: val('nbTest') || undefined,
      rebuildCmd: val('nbRebuild') || undefined,
    }).then(function (r) {
      if (r && r.benchmarkId) nav({ bench: r.benchmarkId });
      else if (err) err.textContent = (r && r.error) || 'failed to start mining';
    }).catch(function () {
      if (err) err.textContent = 'network error — is the server reachable?';
    });
  };
}

function benchCard(b: any, suiteName: string | null): string {
  var diffs = DIFF_ORDER.filter(function (d) { return b.difficulty[d]; })
    .map(function (d) { return '<span class="bench-diff-chip bench-diff-' + d + '">' + b.difficulty[d] + ' ' + DIFF_LABEL[d] + '</span>'; })
    .join('');
  var best = b.best
    ? '<div class="bench-best">Top: <b>' + esc(b.best.model) + '</b> · ' + pct(b.best.resolveRate) +
      (b.best.costPerTask != null ? ' · ' + usd(b.best.costPerTask) + '/task' : '') + '</div>'
    : '<div class="bench-best bench-best-none">No completed runs yet</div>';
  return '<div class="bench-card" data-nav=\'' + esc(JSON.stringify({ bench: b.id })) + '\'>' +
    '<div class="bench-card-top"><span class="bench-card-name">' + esc(b.name) + '</span>' +
    '<span class="badge ' + (b.status === 'frozen' ? 'b-success' : 'b-partial') + '">' + esc(b.status) + '</span></div>' +
    (b.repo ? '<div class="bench-card-repo mono">' + esc(b.repo) + '</div>' : '') +
    '<div class="bench-card-tasks">' + plural(b.taskCount, 'task') + ' · ' + num(b.validatedCount) + ' validated · ' +
    plural(b.runCount, 'run') + '</div>' +
    '<div class="bench-diffs">' + diffs + '</div>' + best +
    // Team + suite chips share the row; team first (who it's for), suite after
    // (how it's grouped). "No team" is a stated fact for an orphaned benchmark
    // (its team was deleted), same idiom as the suites list's "Ungrouped".
    '<div class="bench-suite-chip" title="Team">' + esc(b.teamName || 'No team') + '</div>' +
    (suiteName ? '<div class="bench-suite-chip" title="Suite">' + esc(suiteName) + '</div>' : '') +
    '</div>';
}

function setSeg(root: HTMLElement, seg: string): void {
  benchSeg = seg;
  syncHash();
  renderHome(root);
}

function renderHome(root: HTMLElement): void {
  root.innerHTML = '<div class="empty">Loading benchmarks…</div>';
  // The team picker needs filterOpts.allTeams, but this tab pre-renders at boot
  // and may beat the filter bar's /api/filters fetch — so fetch it ourselves
  // when it isn't there yet (and cache it in the same state slot).
  var optsReady = state.filterOpts
    ? Promise.resolve(state.filterOpts)
    : get('/api/filters').then(function (o) { if (o && !state.filterOpts) state.filterOpts = o; return state.filterOpts; }).catch(function () { return null; });
  Promise.all([
    get('/api/bench/suites' + (benchTeam ? '?team=' + encodeURIComponent(benchTeam) : '')),
    optsReady,
  ]).then(function (rs) {
    var suites = rs[0] || [];
    var real = suites.filter(function (s: any) { return s.id; });
    var flat: any[] = [];
    suites.forEach(function (s: any) {
      s.benchmarks.forEach(function (b: any) { flat.push({ b: b, suite: s.id ? s.name : null }); });
    });

    // Every team, not the viewer's scope-gated list — same allTeams source the
    // Sessions team filter reads (filterOpts is fetched at boot by initFilterBar).
    var teams = (state.filterOpts && state.filterOpts.allTeams) || [];

    // The team facet, next to the seg toggle. Default '' = "All teams": everyone
    // sees everything, so preselecting the viewer's own team would imply a
    // restriction that doesn't exist. Only rendered when there are teams —
    // same guard as the Sessions filter.
    var teamSel = teams.length
      ? '<select class="bench-input" id="benchTeamSel" aria-label="Filter by team">' +
        '<option value="">All teams</option>' +
        teams.map(function (t: any) {
          return '<option value="' + esc(t.id) + '"' + (benchTeam === t.id ? ' selected' : '') + '>' + esc(t.name) + '</option>';
        }).join('') + '</select>'
      : '';

    var seg = '<div class="seg bench-seg">' +
      '<button class="' + (benchSeg === '' ? 'on' : '') + '" id="segBench">Benchmarks</button>' +
      '<button class="' + (benchSeg === 'suites' ? 'on' : '') + '" id="segSuites">Suites</button></div>' + teamSel;

    var html = newBenchForm(real, teams, seg);

    if (!flat.length) {
      // Say which list is empty: a filtered miss reads as a fact about the
      // FILTER, not "no benchmarks exist" — and never hide the control that
      // caused the emptiness, or there's no way back to All teams.
      var teamName = '';
      teams.forEach(function (t: any) { if (t.id === benchTeam) teamName = t.name; });
      html += benchTeam
        ? '<section class="panel"><div class="empty">No benchmarks for ' + esc(teamName || 'this team') + ' yet — switch back to All teams, or create one for them above.</div></section>'
        : '<section class="panel"><div class="empty">No benchmarks yet. A benchmark is a frozen set of tasks mined from ' +
          'your own merged PRs — used to evaluate coding agents, models, and harnesses on your codebase, free of training-data contamination.</div></section>';
    } else if (benchSeg === 'suites') {
      // Grouped view. A benchmark belongs to at most one suite (folder semantics).
      html += '<section class="panel"><div class="panel-head"><h2>Suites</h2>' +
        '<button class="btn" id="suiteNewBtn">New suite</button></div>' +
        '<div class="bench-prompt-meta">A suite groups benchmarks (one suite per benchmark) so an org-level decision — ' +
        '"which model/harness do we standardize on?" — aggregates across the repos that matter.</div>' +
        '<div id="suiteNewForm" style="display:none"><div class="bench-form-row">' +
        '<label>Name <input id="nsName" class="bench-input"></label>' +
        '<label>Description <input id="nsDesc" class="bench-input" style="min-width:280px"></label>' +
        '<button class="btn" id="nsGo">Create</button>' +
        '<button class="btn" id="nsCancel">Cancel</button>' +
        '</div><div id="nsErr" class="bench-prompt-meta" style="color:#b4452f"></div></div></section>';
      suites.forEach(function (s: any) {
        if (!s.benchmarks.length && s.id) {
          html += '<section class="panel"><div class="panel-head"><h2>' + esc(s.name) + '</h2></div>' +
            (s.description ? '<div class="bench-suite-desc">' + esc(s.description) + '</div>' : '') +
            '<div class="empty">No benchmarks in this suite yet — assign one from its benchmark page.</div></section>';
          return;
        }
        if (!s.benchmarks.length) return;
        html += '<section class="panel"><div class="panel-head"><h2>' + (s.id ? esc(s.name) : 'No suite') + '</h2></div>';
        if (s.description) html += '<div class="bench-suite-desc">' + esc(s.description) + '</div>';
        html += '<div class="bench-cards">' + s.benchmarks.map(function (b: any) { return benchCard(b, null); }).join('') + '</div></section>';
      });
    } else {
      // Flat view: every benchmark, newest-last, suite shown as a chip.
      html += '<section class="panel"><div class="panel-head"><h2>All benchmarks</h2></div><div class="bench-cards">' +
        flat.map(function (x) { return benchCard(x.b, x.suite); }).join('') + '</div></section>';
    }

    root.innerHTML = html;
    wireNavLinks(root);
    wireNewBenchForm(root);
    var sb = document.getElementById('segBench');
    var ss = document.getElementById('segSuites');
    if (sb) sb.onclick = function () { setSeg(root, ''); };
    if (ss) ss.onclick = function () { setSeg(root, 'suites'); };
    var tf = document.getElementById('benchTeamSel') as HTMLSelectElement | null;
    if (tf) tf.onchange = function () {
      benchTeam = tf!.value;
      syncHash(); // same URL-mirroring cycle as setSeg
      renderHome(root);
    };
    var snb = document.getElementById('suiteNewBtn');
    var snf = document.getElementById('suiteNewForm');
    if (snb && snf) snb.onclick = function () { snf!.style.display = snf!.style.display === 'none' ? '' : 'none'; };
    var nsc = document.getElementById('nsCancel');
    if (nsc && snf) nsc.onclick = function () { snf!.style.display = 'none'; };
    var nsg = document.getElementById('nsGo');
    if (nsg) nsg.onclick = function () {
      var name = (document.getElementById('nsName') as HTMLInputElement | null)?.value.trim() || '';
      var desc = (document.getElementById('nsDesc') as HTMLInputElement | null)?.value.trim() || '';
      var err = document.getElementById('nsErr');
      if (!name) { if (err) err.textContent = 'name is required'; return; }
      post('/api/bench/suites', { name: name, description: desc || undefined })
        .then(function (r) {
          if (r && r.id) renderHome(root);
          else if (err) err.textContent = (r && r.error) || 'failed to create suite';
        });
    };
  }).catch(function () {
    root.innerHTML = '<div class="empty">Could not load benchmarks — is the server reachable? <a href="javascript:void(0)" id="benchRetry">Retry</a></div>';
    var r = document.getElementById('benchRetry');
    if (r) r.onclick = function () { renderHome(root); };
  });
}

// ---- benchmark detail -------------------------------------------------------

var pollTimer: any = null;

function funnelWidget(f: any, inBenchmark: number): string {
  if (!f) return '';
  var allStages: Array<[string, any, string]> = [
    ['PRs mined', f.mined, 'Every merged PR found in the window'],
    ['Passed filters', f.filtered, 'Touch both source and tests · 3–1500 changed lines · not docs/CI/version-bumps'],
    // Only shown when it actually differs from "Passed filters": the per-mine
    // cap (200 most recent) or a re-mine skipping PRs that are already tasks.
    ['Created as tasks', f.candidates != null && f.candidates !== f.filtered ? f.candidates : null,
      (f.capped ? 'Capped at the ' + num(f.candidates) + ' most recent — ' + num(f.capped) + ' more matched' : 'PRs already mined earlier were skipped')],
    ['Validated', f.validated, 'Fail-to-pass flip confirmed by running the tests in the environment image'],
    ['In this benchmark', inBenchmark || null, 'Tasks currently selected into this benchmark (drop tasks to curate)'],
  ];
  var stages = allStages.filter(function (s) { return s[1] != null; });
  if (!stages.length) return '';
  var max = Number(stages[0][1]) || 1;
  var html = '<div class="bench-funnel"><div class="bench-funnel-title">How ' + num(max) + ' PRs became this benchmark</div>';
  var prev: number | null = null;
  stages.forEach(function (s) {
    var v = Number(s[1]);
    var w = Math.max(3, Math.round((v / max) * 100));
    // The drop annotation gets its own fixed lane so the counts stay
    // right-aligned in a straight column whether or not a row has one.
    var drop = prev != null && prev > v ? '−' + num(prev - v) : '';
    html += '<div class="bench-funnel-row">' +
      '<span class="bench-funnel-lbl">' + esc(s[0]) + '<span class="bench-info" title="' + esc(s[2]) + '">ⓘ</span></span>' +
      '<span class="bar-track"><span class="bar-fill" style="width:' + w + '%"></span></span>' +
      '<span class="bench-funnel-n"><span class="bench-funnel-count">' + num(v) + '</span>' +
      '<span class="bench-funnel-drop">' + drop + '</span></span></div>';
    prev = v;
  });
  return html + '</div>';
}

function progressBar(done: number, total: number): string {
  var pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return '<span class="bench-prog"><span class="bar-track"><span class="bar-fill" style="width:' + pct + '%"></span></span>' +
    '<span class="bench-prog-n">' + num(done) + ' / ' + num(total) + '</span></span>';
}

function pipelineBanner(b: any): string {
  var f = b.funnel || {};
  var st = f.state || '';
  if (!st || st === 'ready') {
    return f.cancelled ? '<div class="notice bench-pipe">Stopped by you — everything generated so far is kept. Click the button again to continue where it left off.</div>' : '';
  }
  if (st === 'error') {
    return '<div class="notice bench-pipe">Pipeline error: ' + esc(f.error || 'unknown') + '</div>';
  }
  var stoppable = st === 'mining' || st === 'generating' || st === 'validating';
  var stopBtn = stoppable ? '<button class="btn bench-stop" id="pipeStop"' + (f.cancel ? ' disabled>Stopping…' : '>Stop</button>') : '';
  var bar = st === 'mining' ? progressBar(f.scanned || 0, f.mined || 0)
    : st === 'generating' ? progressBar(f.promptsDone || 0, f.promptsTotal || 0)
      : st === 'validating' ? progressBar((f.validated || 0) + (f.droppedValidation || 0), (f.validated || 0) + (f.droppedValidation || 0) + (f.validating || 0))
        : '';
  var msg = st === 'queued' ? 'Starting…'
    : st === 'mining' ? 'Mining merged PRs from ' + esc(b.repo || 'the repo') + '…'
      : st === 'mined' ? 'Mining done — ' + num(f.candidates || 0) + ' candidate tasks.' + (f.cancelled ? ' (stopped early by you)' : '')
        : st === 'generating' ? 'Generating task prompts…' + (f.promptsFailed ? ' (' + num(f.promptsFailed) + ' failed)' : '')
          : st === 'validating' ? 'Execution-validating in the environment image… ' +
            num(f.validated || 0) + ' validated · ' + num(f.droppedValidation || 0) + ' dropped'
            : st === 'validated' ? 'Validation ' + (f.cancelled ? 'stopped by you' : 'done') + ' — ' + num(f.validated || 0) + ' tasks validated' +
              (f.droppedValidation ? ', ' + num(f.droppedValidation) + ' dropped (each task records why)' : '') + '.' +
              (f.cancelled ? ' Remaining tasks stayed candidates — click Validate tasks to continue.' : '')
              : esc(st);
  var still = st === 'mined' || st === 'validated';
  return '<div class="notice bench-pipe">' + (still ? '' : '<span class="bench-spin"></span>') + msg + bar + stopBtn + '</div>';
}

/**
 * The guided pipeline: five steps, the first unfinished one is "current", and
 * the hint says exactly what to click next. This is the page's narrator — the
 * action buttons stay in the header, the stepper explains them.
 */
function stepperWidget(b: any): string {
  var tasks = (b.tasks || []) as any[];
  var active = tasks.filter(function (t) { return t.status !== 'dropped'; });
  var candidates = tasks.filter(function (t) { return t.status === 'candidate'; });
  var validated = tasks.filter(function (t) { return t.status === 'validated'; });
  var needPrompts = active.filter(function (t) { return !t.problemStatement; });
  var flagged = active.filter(function (t) { return t.promptSource === 'generated' && t.checkStatus && t.checkStatus !== 'OK'; });
  var frozen = b.status === 'frozen';
  var hasRun = (b.leaderboard || []).length > 0;
  var envReady = !!(b.imageRef && b.testCmd);

  var steps: Array<{ label: string; done: boolean; hint: string }> = [
    { label: 'Mine PRs', done: tasks.length > 0,
      hint: 'Mining is scanning the repo’s merged PRs for changes verified by tests.' },
    { label: 'Task prompts', done: tasks.length > 0 && needPrompts.length === 0,
      hint: 'Click <b>Generate task prompts</b> — each task gets the bug report an agent will solve, leak-checked so it never gives the fix away.' },
    { label: 'Review & validate', done: tasks.length > 0 && candidates.length === 0,
      hint: (flagged.length ? 'Review the <b>' + flagged.length + ' flagged prompt' + (flagged.length > 1 ? 's' : '') + '</b> (LEAK / WRONG / UNSOLVABLE): open each task and regenerate, edit, or drop it. Then click ' : 'Click ') +
        '<b>Validate tasks</b> — each candidate’s tests run in the environment image to prove the fail-to-pass flip.' +
        (envReady ? '' : ' <b>Set the environment image and test command below first.</b>') },
    { label: 'Freeze', done: frozen,
      hint: validated.length
        ? 'Happy with the ' + validated.length + ' validated tasks? Click <b>Freeze</b> to lock the set so every run stays comparable.'
        : 'Freeze locks the task set once validation has produced tasks worth keeping.' },
    { label: 'Run', done: hasRun,
      hint: 'Click <b>New run</b> on the leaderboard: pick a harness + model, and every validated task gets solved and graded in your infrastructure.' },
  ];
  // A running pipeline stage owns its step outright: it is "current" (with a
  // spinner, never a premature checkmark) and the hint describes the work in
  // flight — not the next click. Mining inserts tasks incrementally, so
  // without this override the Mine step would tick green mid-mine.
  var st = (b.funnel || {}).state || '';
  var running: Record<string, [number, string]> = {
    queued: [0, 'Starting…'],
    mining: [0, 'Mining is scanning ' + esc(b.repo || 'the repo') + '’s merged PRs — tasks appear below as they qualify.'],
    generating: [1, 'Writing leak-checked task prompts. Badges appear in the task table as each finishes.'],
    validating: [2, 'Validation is running — every candidate’s tests execute in the environment image; tasks flip to <b>validated</b> or <b>dropped</b> (with the reason) as their sweeps finish.'],
  };
  var run_ = running[st];
  if (run_) steps[run_[0]]!.done = false;

  var current = -1;
  for (var i = 0; i < steps.length; i++) { if (!steps[i]!.done) { current = i; break; } }
  if (run_) current = run_[0];

  var chips = steps.map(function (s, idx) {
    var active = run_ && idx === current;
    var cls = active ? 'current' : s.done ? 'done' : idx === current ? 'current' : 'todo';
    var icon = active ? '<i class="busy"><span class="bench-spin"></span></i>' : '<i>' + (s.done ? '✓' : idx + 1) + '</i>';
    return '<span class="bench-step ' + cls + '">' + icon + esc(s.label) + '</span>';
  }).join('<span class="bench-step-arrow">→</span>');
  var hint = run_ ? run_[1]
    : current === -1
      ? 'All steps complete. Compare configurations on the leaderboard, or add another run.'
      : steps[current]!.hint;
  return '<div class="bench-stepper">' + chips + '</div><div class="bench-step-hint">' + hint + '</div>';
}

/** GitHub-style diff rendering: one card per file, colored line backgrounds. */
function ghDiff(diff: string): string {
  var out = '';
  var body = '';
  var file = '';
  var flush = function () {
    if (file || body) {
      out += '<div class="ghd-file"><div class="ghd-head mono">' + esc(file || '(unknown file)') + '</div>' +
        '<pre class="ghd-body mono">' + body + '</pre></div>';
    }
    body = '';
  };
  diff.split('\n').forEach(function (line) {
    if (line.indexOf('diff --git') === 0) {
      flush();
      var m = / b\/(.+)$/.exec(line);
      file = m ? m[1]! : '';
      return;
    }
    if (line.indexOf('+++') === 0 || line.indexOf('---') === 0 || line.indexOf('index ') === 0) return;
    var cls = line.indexOf('@@') === 0 ? 'ghd-hunk'
      : line.indexOf('+') === 0 ? 'ghd-add'
        : line.indexOf('-') === 0 ? 'ghd-del' : 'ghd-ctx';
    body += '<span class="' + cls + '">' + esc(line || ' ') + '\n</span>';
  });
  flush();
  return out || '<pre class="bench-tx">' + esc(diff) + '</pre>';
}

/** Is a worker/runner actively holding this benchmark? Gates the action buttons. */
function pipelineBusy(b: any): boolean {
  var st = (b.funnel || {}).state || '';
  return st === 'queued' || st === 'mining' || st === 'generating' || st === 'validating';
}

function schedulePoll(root: HTMLElement, id: string): void {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = setTimeout(function () {
    if (state.view === 'benchmarks' && state.bench.bench === id && !state.bench.task && !state.bench.run) {
      renderBenchmark(root, id, true);
    }
  }, 4000);
}

// Suites list cached for the benchmark page's suite selector.
var suitesCache: any[] | null = null;

function renderBenchmark(root: HTMLElement, id: string, background: boolean): void {
  // Background refreshes keep the last-good page up; only a user navigation
  // shows the loading placeholder.
  if (!background) root.innerHTML = '<div class="empty">Loading benchmark…</div>';
  if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
  var suitesP = suitesCache
    ? Promise.resolve(suitesCache)
    : get('/api/bench/suites').then(function (s) {
        suitesCache = (s || []).filter(function (x: any) { return x.id; });
        return suitesCache;
      }).catch(function () { return []; });
  Promise.all([get('/api/bench/benchmarks/' + encodeURIComponent(id)), suitesP]).then(function (rs) {
    var b = rs[0];
    if (!b || b.error) {
      if (!background) root.innerHTML = '<div class="empty">Benchmark not found.</div>';
      return;
    }
    var pill = document.getElementById('benchPill');
    if (pill) pill.style.display = 'none';
    paintBenchmark(root, id, b);
    if (pipelineBusy(b)) schedulePoll(root, id);
  }).catch(function () {
    if (background) {
      // Transient failure mid-poll: keep the page, show a pill, keep retrying.
      var pill = document.getElementById('benchPill');
      if (pill) pill.style.display = '';
      schedulePoll(root, id);
    } else {
      root.innerHTML = '<div class="empty">Could not load this benchmark — is the server reachable? ' +
        '<a href="javascript:void(0)" id="benchRetry">Retry</a></div>';
      var r = document.getElementById('benchRetry');
      if (r) r.onclick = function () { renderBenchmark(root, id, false); };
    }
  });
}

// Per-benchmark toggle for the configuration grid, kept across the 4s repaint
// cycle so a background refresh doesn't undo the user's choice. Unset =
// heuristic: open while setup is incomplete, tucked away once it's done.
var cfgOpenByBench: Record<string, boolean> = {};

// ---- cost/quality scatter + compare state -----------------------------------
// Axis + task-type facet for the scatter, and the two runs picked for compare.
// Module-level so a background repaint keeps the user's selection.
var scatterY = 'resolveRate';
var scatterX = 'costPerTask';
var compareA = '';
var compareB = '';

// Metrics selectable on each axis. `better` drives the Pareto frontier; `get`
// reads either the run's top-level metrics ('all') or one task_type's slice.
interface ScatterMetric { key: string; label: string; short: string; better: 'high' | 'low'; kind: 'pct' | 'usd' | 'ktok'; allOnly?: boolean; }
var Y_METRICS: ScatterMetric[] = [
  { key: 'resolveRate', label: 'Resolve rate (pass@k)', short: 'Resolve rate', better: 'high', kind: 'pct' },
  { key: 'resolveRateFirstTry', label: 'Resolve rate (pass@1)', short: 'pass@1', better: 'high', kind: 'pct', allOnly: true },
];
var X_METRICS: ScatterMetric[] = [
  { key: 'costPerTask', label: 'Cost / task', short: '$ / task', better: 'low', kind: 'usd' },
  { key: 'costPerResolved', label: 'Cost / resolved task', short: '$ / resolved', better: 'low', kind: 'usd' },
  { key: 'tokensPerTask', label: 'Tokens / task', short: 'tok / task', better: 'low', kind: 'ktok' },
];

function metricVal(row: any, key: string): number | null {
  var v = row ? row[key] : null;
  return typeof v === 'number' && isFinite(v) ? v : null;
}
function fmtMetric(v: number | null, kind: string): string {
  if (v == null) return '—';
  if (kind === 'pct') return Math.round(v * 100) + '%';
  if (kind === 'usd') return usd(v);
  if (kind === 'ktok') return num(Math.round(v / 1000)) + 'k';
  return String(v);
}

/** A short, stable colour per run index — cycles a warm-palette set that reads
 *  on the cream background. */
var SCATTER_COLORS = ['#0f7a55', '#b8860b', '#b4452f', '#2f6f9f', '#7a4fa3', '#1c8a8a', '#a3562f', '#5c7a1c'];

/** One labeled cell of the benchmark meta grid. */
function field(label: string, control: string, help?: string): string {
  return '<div class="bench-field"><label>' + esc(label) + '</label>' + control +
    (help ? '<div class="bench-field-help">' + esc(help) + '</div>' : '') + '</div>';
}

function lockedVal(v: string | null): string {
  return v ? '<span class="mono">' + esc(v) + '</span>' : '<span class="bench-unset">not set</span>';
}

// The last-painted benchmark, so scatter/compare controls can re-render their
// own panel in place (no full-page repaint, no refetch) on a dropdown change.
var lastBench: any = null;

// ---- cost/quality scatter ---------------------------------------------------

function scatterPoints(b: any, xKey: string, yKey: string): any[] {
  var pts: any[] = [];
  (b.leaderboard || []).forEach(function (r: any, i: number) {
    var x = metricVal(r, xKey);
    var y = metricVal(r, yKey);
    if (x == null || y == null) return; // a point needs both coordinates
    pts.push({ runId: r.runId, model: r.model, color: SCATTER_COLORS[i % SCATTER_COLORS.length], x: x, y: y, row: r });
  });
  return pts;
}

/** Pareto-optimal run ids: not dominated by any other point on both axes. */
function paretoSet(pts: any[], xBetter: string, yBetter: string): Record<string, boolean> {
  var opt: Record<string, boolean> = {};
  pts.forEach(function (p: any) {
    var dominated = pts.some(function (q: any) {
      if (q === p) return false;
      var xOk = xBetter === 'low' ? q.x <= p.x : q.x >= p.x;
      var yOk = yBetter === 'low' ? q.y <= p.y : q.y >= p.y;
      var strict = (xBetter === 'low' ? q.x < p.x : q.x > p.x) || (yBetter === 'low' ? q.y < p.y : q.y > p.y);
      return xOk && yOk && strict;
    });
    if (!dominated) opt[p.runId] = true;
  });
  return opt;
}

/** Best "value": highest quality per unit cost/token (y ÷ x). */
function bestValueRun(pts: any[]): string | null {
  var best: { id: string; v: number } | null = null;
  pts.forEach(function (p: any) {
    if (p.x > 0 && p.y > 0) {
      var v = p.y / p.x;
      if (!best || v > best.v) best = { id: p.runId, v: v };
    }
  });
  return best ? best.id : null;
}

function niceDomain(vals: number[], kind: string, better: string): [number, number] {
  var lo = Math.min.apply(null, vals), hi = Math.max.apply(null, vals);
  if (kind === 'usd' || kind === 'ktok') lo = 0; // cost/token axes anchor at 0
  if (lo === hi) { // single value / all-equal: pad so the point isn't on an edge
    var pad = Math.abs(hi) * 0.2 || (kind === 'pct' ? 0.1 : 1);
    lo -= pad; hi += pad;
  } else {
    var m = (hi - lo) * 0.12;
    lo -= m; hi += m;
  }
  if (kind === 'pct') { lo = Math.max(0, lo); hi = Math.min(1, hi); if (hi <= lo) { lo = 0; hi = 1; } }
  if ((kind === 'usd' || kind === 'ktok') && lo < 0) lo = 0;
  return [lo, hi];
}

function buildScatterPlot(id: string, b: any): string {
  var ym = Y_METRICS.filter(function (m) { return m.key === scatterY; })[0] || Y_METRICS[0];
  var xm = X_METRICS.filter(function (m) { return m.key === scatterX; })[0] || X_METRICS[0];
  var pts = scatterPoints(b, xm.key, ym.key);
  if (!pts.length) {
    return '<div class="empty">No runs have both ' + esc(ym.short) + ' and ' + esc(xm.short) +
      ' yet — they populate as runs finish.</div>';
  }
  var W = 760, H = 420, padL = 66, padR = 118, padT = 22, padB = 54;
  var plotW = W - padL - padR, plotH = H - padT - padB;
  var xd = niceDomain(pts.map(function (p) { return p.x; }), xm.kind, xm.better);
  var yd = niceDomain(pts.map(function (p) { return p.y; }), ym.kind, ym.better);
  var sx = function (v: number) { return padL + (xd[1] === xd[0] ? 0.5 : (v - xd[0]) / (xd[1] - xd[0])) * plotW; };
  var sy = function (v: number) { return padT + (1 - (yd[1] === yd[0] ? 0.5 : (v - yd[0]) / (yd[1] - yd[0]))) * plotH; };
  var pareto = paretoSet(pts, xm.better, ym.better);
  var bestId = bestValueRun(pts);

  var svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" class="bench-scatter-svg" preserveAspectRatio="xMidYMid meet">';
  // gridlines + ticks (4 divisions per axis)
  var ticks = 4, i;
  for (i = 0; i <= ticks; i++) {
    var yv = yd[0] + (yd[1] - yd[0]) * (i / ticks);
    var py = sy(yv);
    svg += '<line x1="' + padL + '" y1="' + py + '" x2="' + (padL + plotW) + '" y2="' + py + '" class="bench-scatter-grid"/>';
    svg += '<text x="' + (padL - 10) + '" y="' + (py + 4) + '" class="bench-scatter-tick" text-anchor="end">' + esc(fmtMetric(yv, ym.kind)) + '</text>';
    var xv = xd[0] + (xd[1] - xd[0]) * (i / ticks);
    var px = sx(xv);
    svg += '<line x1="' + px + '" y1="' + padT + '" x2="' + px + '" y2="' + (padT + plotH) + '" class="bench-scatter-grid"/>';
    svg += '<text x="' + px + '" y="' + (padT + plotH + 20) + '" class="bench-scatter-tick" text-anchor="middle">' + esc(fmtMetric(xv, xm.kind)) + '</text>';
  }
  // axis titles
  svg += '<text x="' + (padL + plotW / 2) + '" y="' + (H - 8) + '" class="bench-scatter-axis" text-anchor="middle">' + esc(xm.label) + ' →</text>';
  svg += '<text x="16" y="' + (padT + plotH / 2) + '" class="bench-scatter-axis" text-anchor="middle" transform="rotate(-90 16 ' + (padT + plotH / 2) + ')">' + esc(ym.label) + ' →</text>';
  // points
  pts.forEach(function (p: any) {
    var cx = sx(p.x), cy = sy(p.y);
    var isBest = p.runId === bestId, onFront = !!pareto[p.runId];
    if (onFront) svg += '<circle cx="' + cx + '" cy="' + cy + '" r="10" fill="none" stroke="' + p.color + '" stroke-opacity="0.5" stroke-width="2"/>';
    svg += '<g class="bench-scatter-pt" data-run="' + esc(p.runId) +
      '" data-tip="' + esc(p.model + ' • ' + ym.short + ' ' + fmtMetric(p.y, ym.kind) + ' • ' + xm.short + ' ' + fmtMetric(p.x, xm.kind) + ' • solved ' + num(p.row.solved) + '/' + num(p.row.tasks)) + '">' +
      '<circle cx="' + cx + '" cy="' + cy + '" r="6" fill="' + p.color + '" stroke="#fffdf9" stroke-width="1.5"/>' +
      (isBest ? '<text x="' + cx + '" y="' + (cy - 13) + '" class="bench-scatter-best" text-anchor="middle">★</text>' : '') +
      '<text x="' + (cx + 10) + '" y="' + (cy + 4) + '" class="bench-scatter-lbl">' + esc(p.model) + '</text>' +
      '</g>';
  });
  svg += '</svg>';

  var legend = '<div class="bench-scatter-legend">' +
    '<span class="bench-scatter-key"><span class="bench-scatter-ring"></span>Pareto frontier (no run is both cheaper and better)</span>' +
    (bestId ? '<span class="bench-scatter-key"><span class="bench-scatter-star">★</span>Best value (' + esc(ym.short) + ' per ' + esc(xm.short) + ')</span>' : '') +
    '<span class="bench-scatter-key bench-scatter-hint">click a point → open the run</span></div>';
  return svg + legend;
}

function buildScatterPanel(id: string, b: any): string {
  if (!b.leaderboard || !b.leaderboard.length) return '';
  var yOpts = Y_METRICS.map(function (m) { return '<option value="' + m.key + '"' + (scatterY === m.key ? ' selected' : '') + '>' + esc(m.label) + '</option>'; }).join('');
  var xOpts = X_METRICS.map(function (m) { return '<option value="' + m.key + '"' + (scatterX === m.key ? ' selected' : '') + '>' + esc(m.label) + '</option>'; }).join('');
  return '<section class="panel"><div class="panel-head"><h2>Cost vs. quality</h2></div>' +
    '<div class="bench-prompt-meta">Each point is a run. Up-and-left is better — high quality, low cost. The trade-off the model-routing question turns on.</div>' +
    '<div class="bench-scatter-ctl">' +
    '<label>Y <select id="scatterY" class="bench-input bench-input-sm">' + yOpts + '</select></label>' +
    '<span class="bench-scatter-vs">vs</span>' +
    '<label>X <select id="scatterX" class="bench-input bench-input-sm">' + xOpts + '</select></label>' +
    '</div>' +
    '<div class="bench-scatter-wrap"><div id="scatterPlot">' + buildScatterPlot(id, b) + '</div>' +
    '<div id="scatterTip" class="bench-scatter-tip" style="display:none"></div></div>' +
    '</section>';
}

function wireScatterPoints(root: HTMLElement, id: string): void {
  var tip = root.querySelector('#scatterTip') as HTMLElement | null;
  Array.prototype.forEach.call(root.querySelectorAll('.bench-scatter-pt'), function (g: Element) {
    var el = g as HTMLElement;
    el.onclick = function () { nav({ bench: id, run: el.getAttribute('data-run') || '' }); };
    el.onmouseenter = function (e: Event) {
      if (!tip) return;
      tip.textContent = el.getAttribute('data-tip') || '';
      tip.style.display = '';
      var me = e as MouseEvent;
      tip.style.left = (me.clientX + 14) + 'px';
      tip.style.top = (me.clientY + 14) + 'px';
    };
    el.onmousemove = function (e: Event) {
      if (!tip) return;
      var me = e as MouseEvent;
      tip.style.left = (me.clientX + 14) + 'px';
      tip.style.top = (me.clientY + 14) + 'px';
    };
    el.onmouseleave = function () { if (tip) tip.style.display = 'none'; };
  });
}

function wireScatter(root: HTMLElement, id: string): void {
  var refresh = function () {
    var plot = root.querySelector('#scatterPlot') as HTMLElement | null;
    if (plot && lastBench) { plot.innerHTML = buildScatterPlot(id, lastBench); wireScatterPoints(root, id); }
  };
  var yS = root.querySelector('#scatterY') as HTMLSelectElement | null;
  var xS = root.querySelector('#scatterX') as HTMLSelectElement | null;
  if (yS) yS.onchange = function () { scatterY = yS!.value; refresh(); };
  if (xS) xS.onchange = function () { scatterX = xS!.value; refresh(); };
  wireScatterPoints(root, id);
}

// ---- run compare (task-level delta) -----------------------------------------

function buildComparePanel(id: string, b: any): string {
  var runs = b.leaderboard || [];
  if (runs.length < 2) return ''; // needs two runs to diff
  if (!compareA) compareA = runs[0].runId;
  if (!compareB) compareB = runs[1] ? runs[1].runId : runs[0].runId;
  var opts = function (sel: string) {
    return runs.map(function (r: any) { return '<option value="' + esc(r.runId) + '"' + (sel === r.runId ? ' selected' : '') + '>' + esc(r.model) + '</option>'; }).join('');
  };
  return '<section class="panel"><div class="panel-head"><h2>Compare runs</h2></div>' +
    '<div class="bench-prompt-meta">Swap one model for another and see which tasks flip resolved ↔ unresolved, and the cost delta — the routing decision, made concrete.</div>' +
    '<div class="bench-scatter-ctl">' +
    '<label>A <select id="cmpA" class="bench-input bench-input-sm">' + opts(compareA) + '</select></label>' +
    '<span class="bench-scatter-vs">vs</span>' +
    '<label>B <select id="cmpB" class="bench-input bench-input-sm">' + opts(compareB) + '</select></label>' +
    '<button class="btn" id="cmpGo">Compare</button>' +
    '</div><div id="cmpResult"></div></section>';
}

function renderCompareResult(root: HTMLElement): void {
  var box = root.querySelector('#cmpResult') as HTMLElement | null;
  if (!box) return;
  if (compareA === compareB) { box.innerHTML = '<div class="empty">Pick two different runs.</div>'; return; }
  box.innerHTML = '<div class="empty"><span class="bench-spin"></span>Comparing…</div>';
  get('/api/bench/compare?a=' + encodeURIComponent(compareA) + '&b=' + encodeURIComponent(compareB)).then(function (r: any) {
    if (!r || r.error) { box.innerHTML = '<div class="empty">' + esc((r && r.error) || 'compare failed') + '</div>'; return; }
    var flips = r.tasks.filter(function (t: any) { return t.flipped; });
    var gained = flips.filter(function (t: any) { return t.b && t.b.resolved; }).length;
    var lost = flips.filter(function (t: any) { return t.a && t.a.resolved; }).length;
    var sumCost = function (side: string) { return r.tasks.reduce(function (s: number, t: any) { return s + ((t[side] && t[side].cost) || 0); }, 0); };
    var costA = sumCost('a'), costB = sumCost('b');
    var summ = '<div class="bench-cmp-summary">' +
      '<span class="badge b-success">+' + gained + ' gained</span>' +
      '<span class="badge b-failure">−' + lost + ' lost</span>' +
      '<span class="bench-cmp-cost">B spend ' + usd(costB) + ' vs A ' + usd(costA) +
      (costA > 0 ? ' (' + (costB < costA ? '−' : '+') + Math.round(Math.abs(costB - costA) / costA * 100) + '%)' : '') + '</span></div>';
    var cell = function (c: any): string {
      if (!c) return '<td class="num">—</td>';
      return '<td class="num">' + (c.resolved ? '<span class="badge b-success">✓</span>' : '<span class="badge b-failure">✗</span>') + '</td>';
    };
    var rows = r.tasks.map(function (t: any): string {
      return '<tr' + (t.flipped ? ' class="bench-cmp-flip"' : '') + '>' +
        '<td class="bench-task-title">' + esc(t.prTitle || t.taskId.slice(0, 8)) + '</td>' +
        cell(t.a) + cell(t.b) +
        '<td>' + (t.flipped ? (t.b && t.b.resolved ? '<span class="badge b-success">gained</span>' : '<span class="badge b-failure">lost</span>') : '') + '</td></tr>';
    }).join('');
    box.innerHTML = summ +
      '<table class="bench-tbl"><thead><tr><th>Task</th><th>' + esc(r.a.model) + ' (A)</th><th>' + esc(r.b.model) + ' (B)</th><th>Δ</th></tr></thead><tbody>' +
      rows + '</tbody></table>';
  });
}

function wireCompare(root: HTMLElement, id: string): void {
  var aS = root.querySelector('#cmpA') as HTMLSelectElement | null;
  var bS = root.querySelector('#cmpB') as HTMLSelectElement | null;
  var go = root.querySelector('#cmpGo') as HTMLElement | null;
  if (aS) aS.onchange = function () { compareA = aS!.value; };
  if (bS) bS.onchange = function () { compareB = bS!.value; };
  if (go) go.onclick = function () { renderCompareResult(root); };
}

function paintBenchmark(root: HTMLElement, id: string, b: any): void {
  lastBench = b;
  var frozen = b.status === 'frozen';
  var busy = pipelineBusy(b);
  var dis = busy ? ' disabled title="Wait for the current pipeline stage to finish"' : '';
  var suites = suitesCache || [];
  var fun = b.funnel || {};
  var html = '<span class="bench-recon" id="benchPill" style="display:none"><span class="bench-spin"></span>reconnecting…</span>';

  html += '<section class="panel"><div class="bench-head">' +
    backArrow('All benchmarks', {}) +
    '<input class="bench-name-input" id="benchName" value="' + esc(b.name) + '"' + (frozen ? ' disabled' : '') + '>' +
    '<span class="badge ' + (frozen ? 'b-success' : 'b-partial') + '" title="' +
    (frozen
      ? 'Frozen ' + esc(dayOf(b.frozenAt || '')) + ' — the task set, prompts, and environment are locked so run results stay comparable.'
      : 'Draft — tasks, prompts, and environment are still editable. Freeze before comparing runs.') + '">' + esc(b.status) + '</span>' +
    (frozen ? '<button class="btn" id="benchUnfreeze" title="Back to draft. Existing runs were measured against this frozen set — results stop being comparable once tasks change.">Unfreeze</button>' : '') +
    (frozen ? '' : '<button class="btn" id="benchRemine"' + dis + (busy ? '' : ' title="Scan the PR window again — already-mined PRs are skipped, new ones become tasks"') + '>' +
      (fun.cancelled && fun.state === 'mined' ? 'Resume mining' : 'Re-mine') + '</button>') +
    (frozen ? '' : '<button class="btn" id="benchGenPrompts"' + dis + (busy ? '' : ' title="Write leak-checked task prompts for tasks that lack one"') + '>Generate task prompts</button>') +
    (frozen ? '' : '<button class="btn" id="benchValidate"' + dis + (busy ? '' : ' title="Run each candidate’s tests at the fix and pre-fix commits in the environment image; the fail-to-pass flip validates the task"') + '>Validate tasks</button>') +
    (frozen ? '' : '<button class="btn" id="benchFreeze"' + dis + (busy ? '' : ' title="Freezing locks the task set so run results stay comparable"') + '>Freeze</button>') +
    (frozen ? '' : '<button class="btn bench-danger" id="benchDelete"' + dis + (busy ? '' : ' title="Delete this draft benchmark"') + '>Delete</button>') +
    '<button class="btn" id="benchSave" style="display:none">Save</button>' +
    '</div>' +
    '<textarea class="bench-desc-input" id="benchDesc" rows="2" placeholder="Description…"' + (frozen ? ' disabled' : '') + '>' + esc(b.description || '') + '</textarea>' +
    '<div class="bench-stage">' + pipelineBanner(b) + stepperWidget(b) + '</div>';

  var cfgOpen = cfgOpenByBench[id] != null
    ? cfgOpenByBench[id]!
    : !(b.imageRef && b.testCmd && b.tasks.length); // setup done → start collapsed
  var cfgSummary = '<span class="mono">' + esc(b.repo || '—') + '</span>' +
    (b.windowFrom ? ' · ' + esc(dayOf(b.windowFrom)) + ' → ' + esc(dayOf(b.windowTo || '')) : '') +
    ' · ' + (b.imageRef ? '<span class="mono">' + esc(b.imageRef) + '</span>' : '<span class="bench-unset">image not set</span>') +
    (b.testCmd ? '' : ' · <span class="bench-unset">test command not set</span>');
  html += '<div class="bench-cfg">' +
    '<button class="bench-cfg-head" id="cfgToggle"><span class="bench-cfg-chev">' + (cfgOpen ? '▾' : '▸') + '</span>Configuration' +
    (cfgOpen ? '' : '<span class="bench-cfg-sum">' + cfgSummary + '</span>') + '</button>' +
    (cfgOpen ? '<div class="bench-grid">' +
    field('Repo', '<span class="mono">' + esc(b.repo || '—') + '</span>') +
    field('PR window', b.windowFrom ? esc(dayOf(b.windowFrom)) + ' → ' + esc(dayOf(b.windowTo || '')) : '—') +
    field('Suite', '<select id="benchSuite" class="bench-input">' +
      '<option value="">(none)</option>' +
      suites.map(function (s: any) {
        return '<option value="' + esc(s.id) + '"' + (b.suiteId === s.id ? ' selected' : '') + '>' + esc(s.name) + '</option>';
      }).join('') + '</select>') +
    field('Environment image', frozen
      ? lockedVal(b.imageRef)
      : '<input id="benchImage" class="bench-input mono" placeholder="registry/ci-image:tag" value="' + esc(b.imageRef || '') + '">',
      'The CI/dev image validation and runs execute in. Needs git + the repo’s test dependencies — your existing CI image works.') +
    field('Test command', frozen
      ? lockedVal(b.testCmd)
      : '<input id="benchTest" class="bench-input mono" placeholder="python -m pytest {files} --junitxml={out}" value="' + esc(b.testCmd || '') + '">',
      '{files} = the task’s test files · {out} = junit XML the runner reads. Any runner that emits junit works.') +
    field('Rebuild command', frozen
      ? lockedVal(b.rebuildCmd)
      : '<input id="benchRebuild" class="bench-input mono" placeholder="pip install -e . (optional)" value="' + esc(b.rebuildCmd || '') + '">',
      'Runs after every checkout, before tests — re-syncs dependencies when the commit changed them.') +
    field('Mining filters',
      '<span class="mono">tests: ' + esc((b.testPaths || []).join(', ') || 'auto') + ' · excluded: ' + esc((b.excludePaths || []).join(', ') || '—') + '</span>',
      'Files under test paths (or common conventions) count as tests; other code files count as source; a PR must touch both.') +
    '</div>' : '') +
    '</div>' +
    funnelWidget(b.funnel, b.tasks.length) + '</section>';

  // Leaderboard + run launcher (UI-first: runs are created here and picked up
  // by the runner; no CLI needed).
  html += '<section class="panel"><div class="panel-head"><h2>Leaderboard</h2>' +
    '<button class="btn" id="runNewBtn">New run</button></div>' +
    '<div id="runNewForm" style="display:none"><div class="bench-form-row">' +
    '<label>Harness <select id="nrHarness" class="bench-input">' +
    '<option value="claude-code">claude-code</option><option value="openhands" disabled>openhands (adapter coming)</option><option value="codex-cli" disabled>codex-cli (adapter coming)</option>' +
    '</select></label>' +
    '<label>Model <input id="nrModel" class="bench-input" placeholder="model id your credentials accept" style="min-width:220px" title="Bedrock: us.anthropic.claude-sonnet-5 · Anthropic API: claude-sonnet-5"></label>' +
    '<label>Attempts / task <input id="nrAttempts" type="number" min="1" max="10" value="1" class="bench-input" style="min-width:70px"></label>' +
    '<label>Temperature <input id="nrTemp" type="number" min="0" max="2" step="0.1" value="0.7" class="bench-input" style="min-width:70px"></label>' +
    '<button class="btn" id="nrGo">Start run</button>' +
    '<button class="btn" id="nrCancel">Cancel</button>' +
    '</div>' +
    '<div class="bench-prompt-meta">A run takes one harness + model through every task, in your infrastructure, against the environment image. ' +
    (frozen ? '' : 'Tip: freeze the benchmark first so results stay comparable as tasks change. ') +
    'Attempts appear on the run page as they finish.</div>' +
    '<div id="nrErr" class="bench-prompt-meta" style="color:#b4452f"></div></div>';
  if (!b.leaderboard.length) {
    html += '<div class="empty">No runs yet — start one with <b>New run</b>. Each configuration you run lands here as a leaderboard row: resolve rate, cost per task, tokens per task.</div>';
  } else {
    html += '<table class="bench-tbl"><thead><tr><th></th><th>Harness</th><th>Model</th>' +
      '<th title="A task counts as solved if any of its attempts resolves (pass@k). The denominator is the benchmark’s validated task count — the same for every run.">Resolve rate</th>' +
      '<th>Solved</th><th>$ / task</th>' +
      '<th title="Total spend ÷ tasks solved — the routing-decision metric. A cheap model that rarely solves isn’t cheap.">$ / resolved</th>' +
      '<th>Tokens / task</th></tr></thead><tbody>';
    b.leaderboard.forEach(function (r: any, i: number) {
      var live = r.status === 'running' || r.status === 'queued';
      var extras = '';
      if (live) extras += '<div class="bench-sofar"><span class="bench-spin"></span>so far — ' + num(r.attempted) + ' of ' + num(r.tasks) + ' tasks attempted</div>';
      if (r.attemptsPerTask > 1) extras += '<div class="bench-sofar">pass@1: ' + pct(r.resolveRateFirstTry) + ' · pass@' + r.attemptsPerTask + ' shown</div>';
      html += '<tr class="bench-row" data-nav=\'' + esc(JSON.stringify({ bench: id, run: r.runId })) + '\'>' +
        '<td class="bench-rank">' + (r.resolveRate != null && !live ? '#' + (i + 1) : '') + '</td>' +
        '<td>' + esc(r.harness) + '</td><td><b>' + esc(r.model) + '</b>' +
        (r.status !== 'done' ? ' <span class="badge b-partial">' + esc(r.status) + '</span>' : '') + '</td>' +
        '<td>' + rateBar(r.resolveRate) + extras + '</td>' +
        '<td class="num">' + num(r.solved) + ' / ' + num(r.tasks) + '</td>' +
        '<td class="num">' + (r.costPerTask != null ? usd(r.costPerTask) : '—') + '</td>' +
        '<td class="num">' + (r.costPerResolved != null ? usd(r.costPerResolved) : '—') + '</td>' +
        '<td class="num">' + (r.tokensPerTask != null ? num(Math.round(r.tokensPerTask / 1000)) + 'k' : '—') + '</td></tr>';
    });
    html += '</tbody></table>';
  }
  html += '</section>';

  // Cost/quality scatter + run compare (only meaningful once runs exist).
  html += buildScatterPanel(id, b);
  html += buildComparePanel(id, b);

  // Tasks
  html += '<section class="panel"><div class="panel-head"><h2>Tasks (' + b.tasks.length + ')</h2>' +
    (frozen ? '' : '<button class="btn bench-danger" id="bulkDrop" style="display:none">Drop selected</button>') +
    '</div>';
  if (!b.tasks.length) html += '<div class="empty">No tasks in this benchmark yet.</div>';
  else {
    html += '<table class="bench-tbl"><thead><tr>' +
      (frozen ? '' : '<th class="bench-selcol"><input type="checkbox" id="taskSelAll" title="Select all"></th>') +
      '<th>PR</th><th>Title</th><th>Difficulty</th>' +
      '<th>Task prompt</th><th title="Fail-to-pass tests — discovered by the execution-validation sweep; “—” until validation runs.">F2P</th><th>Lines</th><th>Status</th></tr></thead><tbody>';
    b.tasks.forEach(function (t: any) {
      html += '<tr class="bench-row" data-nav=\'' + esc(JSON.stringify({ bench: id, task: t.id })) + '\'>' +
        (frozen ? '' : '<td class="bench-selcol"><input type="checkbox" class="taskSel" data-tid="' + esc(t.id) + '"></td>') +
        '<td class="mono">#' + esc(t.prNumber != null ? String(t.prNumber) : '') + '</td>' +
        '<td class="bench-task-title">' + esc(t.prTitle || '(untitled)') + '</td>' +
        '<td>' + diffBadge(t.difficulty) + '</td>' +
        '<td>' + promptBadge(t) + '</td>' +
        '<td class="num">' + (t.f2p.length ? t.f2p.length : '—') + '</td>' +
        '<td class="num">' + (t.changedLines != null ? num(t.changedLines) : '—') + '</td>' +
        '<td>' + statusBadge(t.status) + '</td></tr>';
    });
    html += '</tbody></table>';
  }
  html += '<div class="empty" style="margin-top:10px">Every task is a real merged PR replayed as a challenge: the agent starts from the pre-fix commit and must make the held-out fail-to-pass tests pass without breaking the pass-to-pass set.</div></section>';

  // Preserve scroll across background repaints (PM #2).
  var y = window.scrollY;
  root.innerHTML = html;
  window.scrollTo(0, y);
  wireNavLinks(root);
  wireScatter(root, id);
  wireCompare(root, id);

  var nameEl = document.getElementById('benchName') as HTMLInputElement | null;
  var descEl = document.getElementById('benchDesc') as HTMLTextAreaElement | null;
  var imgEl = document.getElementById('benchImage') as HTMLInputElement | null;
  var testEl = document.getElementById('benchTest') as HTMLInputElement | null;
  var rebuildEl = document.getElementById('benchRebuild') as HTMLInputElement | null;
  var saveEl = document.getElementById('benchSave') as HTMLElement | null;
  var dirty = function () { if (saveEl) saveEl.style.display = ''; };
  if (nameEl) nameEl.oninput = dirty;
  if (descEl) descEl.oninput = dirty;
  if (imgEl) imgEl.oninput = dirty;
  if (testEl) testEl.oninput = dirty;
  if (rebuildEl) rebuildEl.oninput = dirty;
  if (saveEl) saveEl.onclick = function () {
    patch('/api/bench/benchmarks/' + encodeURIComponent(id), {
      name: nameEl ? nameEl.value : undefined,
      description: descEl ? descEl.value : undefined,
      imageRef: imgEl ? imgEl.value : undefined,
      testCmd: testEl ? testEl.value : undefined,
      rebuildCmd: rebuildEl ? rebuildEl.value : undefined,
    }).then(function () { flashSaved(saveEl, function () { renderBenchmark(root, id, true); }); });
  };
  var cfgEl = document.getElementById('cfgToggle');
  if (cfgEl) cfgEl.onclick = function () {
    cfgOpenByBench[id] = !cfgOpen;
    paintBenchmark(root, id, b);
  };
  var stopEl = document.getElementById('pipeStop') as HTMLButtonElement | null;
  if (stopEl && !stopEl.disabled) stopEl.onclick = function () {
    stopEl!.disabled = true;
    stopEl!.textContent = 'Stopping…';
    post('/api/bench/benchmarks/' + encodeURIComponent(id) + '/cancel');
  };
  var valEl = document.getElementById('benchValidate') as HTMLButtonElement | null;
  if (valEl && !valEl.disabled) valEl.onclick = function () {
    post('/api/bench/benchmarks/' + encodeURIComponent(id) + '/validate').then(function (r) {
      if (r && r.error) { valEl!.textContent = r.error; setTimeout(function () { if (valEl) valEl.textContent = 'Validate tasks'; }, 4000); }
      else renderBenchmark(root, id, false);
    });
  };
  var suiteEl = document.getElementById('benchSuite') as HTMLSelectElement | null;
  if (suiteEl) suiteEl.onchange = function () {
    patch('/api/bench/benchmarks/' + encodeURIComponent(id), { suiteId: suiteEl!.value || null })
      .then(function () { renderBenchmark(root, id, true); });
  };
  var freezeEl = document.getElementById('benchFreeze') as HTMLButtonElement | null;
  if (freezeEl && !freezeEl.disabled) freezeEl.onclick = function () {
    post('/api/bench/benchmarks/' + encodeURIComponent(id) + '/freeze').then(function () { renderBenchmark(root, id, false); });
  };
  var unfreezeEl = document.getElementById('benchUnfreeze') as HTMLButtonElement | null;
  if (unfreezeEl) unfreezeEl.onclick = function () {
    if (unfreezeEl!.getAttribute('data-armed')) {
      post('/api/bench/benchmarks/' + encodeURIComponent(id) + '/unfreeze').then(function () { renderBenchmark(root, id, false); });
    } else {
      unfreezeEl!.setAttribute('data-armed', '1');
      unfreezeEl!.textContent = 'Runs stay but stop being comparable — unfreeze?';
      setTimeout(function () { if (unfreezeEl) { unfreezeEl.removeAttribute('data-armed'); unfreezeEl.textContent = 'Unfreeze'; } }, 4000);
    }
  };
  var rnb = document.getElementById('runNewBtn');
  var rnf = document.getElementById('runNewForm');
  if (rnb && rnf) rnb.onclick = function () { rnf!.style.display = rnf!.style.display === 'none' ? '' : 'none'; };
  var rnc = document.getElementById('nrCancel');
  if (rnc && rnf) rnc.onclick = function () { rnf!.style.display = 'none'; };
  var rng = document.getElementById('nrGo');
  if (rng) rng.onclick = function () {
    var val = function (i: string) { var el = document.getElementById(i) as HTMLInputElement | null; return el ? el.value.trim() : ''; };
    var err = document.getElementById('nrErr');
    var model = val('nrModel');
    if (!model) { if (err) err.textContent = 'model is required — the id your harness credentials accept (hover the field for examples)'; return; }
    post('/api/bench/benchmarks/' + encodeURIComponent(id) + '/runs', {
      harness: val('nrHarness'), model: model,
      attempts: parseInt(val('nrAttempts') || '1', 10),
      temperature: parseFloat(val('nrTemp') || '0.7'),
    }).then(function (r) {
      if (r && r.runId) nav({ bench: id, run: r.runId });
      else if (err) err.textContent = (r && r.error) || 'failed to create run';
    });
  };
  var genEl = document.getElementById('benchGenPrompts') as HTMLButtonElement | null;
  if (genEl && !genEl.disabled) genEl.onclick = function () {
    post('/api/bench/benchmarks/' + encodeURIComponent(id) + '/generate-prompts').then(function () { renderBenchmark(root, id, false); });
  };
  var remineEl = document.getElementById('benchRemine') as HTMLButtonElement | null;
  if (remineEl && !remineEl.disabled) remineEl.onclick = function () {
    post('/api/bench/benchmarks/' + encodeURIComponent(id) + '/mine').then(function () { renderBenchmark(root, id, false); });
  };
  // Bulk task selection: checkboxes must not trigger the row's navigation.
  var selBoxes = root.querySelectorAll('.taskSel') as NodeListOf<HTMLInputElement>;
  var bulkEl = document.getElementById('bulkDrop') as HTMLButtonElement | null;
  var syncBulk = function () {
    var n = 0;
    selBoxes.forEach(function (cb) { if (cb.checked) n++; });
    if (bulkEl) {
      bulkEl.style.display = n ? '' : 'none';
      bulkEl.textContent = 'Drop ' + n + ' selected';
    }
  };
  selBoxes.forEach(function (cb) {
    cb.onclick = function (e) { e.stopPropagation(); syncBulk(); };
  });
  var allEl = document.getElementById('taskSelAll') as HTMLInputElement | null;
  if (allEl) allEl.onclick = function (e) {
    e.stopPropagation();
    selBoxes.forEach(function (cb) { cb.checked = allEl!.checked; });
    syncBulk();
  };
  if (bulkEl) bulkEl.onclick = function () {
    var ids: string[] = [];
    selBoxes.forEach(function (cb) { if (cb.checked) ids.push(cb.getAttribute('data-tid') || ''); });
    if (!ids.length) return;
    bulkEl!.disabled = true;
    Promise.all(ids.map(function (tid) {
      return patch('/api/bench/tasks/' + encodeURIComponent(tid), { status: 'dropped' });
    })).then(function () { renderBenchmark(root, id, false); });
  };
  var delEl = document.getElementById('benchDelete') as HTMLButtonElement | null;
  if (delEl && !delEl.disabled) delEl.onclick = function () {
    if (delEl!.getAttribute('data-armed')) {
      fetch('/api/bench/benchmarks/' + encodeURIComponent(id), { method: 'DELETE' }).then(function () { nav({}); });
    } else {
      delEl!.setAttribute('data-armed', '1');
      delEl!.textContent = 'Really delete?';
      setTimeout(function () { if (delEl) { delEl.removeAttribute('data-armed'); delEl.textContent = 'Delete'; } }, 3000);
    }
  };
}

// ---- task detail ------------------------------------------------------------

function renderTask(root: HTMLElement, id: string): void {
  var benchId = state.bench.bench;
  root.innerHTML = '<div class="empty">Loading task…</div>';
  // The parent benchmark's frozen state gates editing (frozen = immutable).
  var benchP = benchId
    ? get('/api/bench/benchmarks/' + encodeURIComponent(benchId)).catch(function () { return null; })
    : Promise.resolve(null);
  Promise.all([get('/api/bench/tasks/' + encodeURIComponent(id)), benchP]).then(function (rs) {
    var t = rs[0]; var parent = rs[1];
    if (!t || t.error) { root.innerHTML = '<div class="empty">Task not found.</div>'; return; }
    var frozen = !!(parent && parent.status === 'frozen');
    var dis = frozen ? ' disabled' : '';

    var html = '<section class="panel"><div class="bench-head">' +
      backArrow('Back to benchmark', { bench: benchId }) +
      '<h2 class="bench-task-h">' + (t.prNumber != null ? '<a href="' + esc(t.prUrl || '#') + '" target="_blank" rel="noopener" class="mono">#' + t.prNumber + '</a> ' : '') + esc(t.prTitle || '(untitled)') + '</h2>' +
      statusBadge(t.status) + '</div>' +
      (frozen ? '<div class="bench-prompt-meta">Part of a frozen benchmark — task fields are locked.</div>' : '') +
      (t.validationError ? '<div class="notice bench-pipe">Validation ' + (t.status === 'dropped' ? 'dropped this task' : 'note') + ': ' + esc(t.validationError) + '</div>' : '') +
      '<div class="bench-meta">' +
      (t.mergedAt ? '<span class="kv"><b>Merged</b> ' + esc(dayOf(t.mergedAt)) + '</span>' : '') +
      (t.baseCommit ? '<span class="kv"><b>Base</b> <span class="mono">' + esc(t.baseCommit.slice(0, 10)) + '</span></span>' : '') +
      (t.goldSha ? '<span class="kv"><b>Gold</b> <span class="mono">' + esc(t.goldSha.slice(0, 10)) + '</span></span>' : '') +
      (t.changedLines != null ? '<span class="kv"><b>Diff</b> ' + num(t.changedLines) + ' lines</span>' : '') +
      '</div>' +
      '<div class="bench-ctl-row">' +
      '<label>Difficulty <select id="taskDiff"' + dis + '>' + DIFF_ORDER.map(function (d) {
        return '<option value="' + d + '"' + (t.difficulty === d ? ' selected' : '') + '>' + DIFF_LABEL[d] + '</option>';
      }).join('') + '</select></label>' +
      '<label>Status <select id="taskStatus"' + dis + '>' + ['candidate', 'validated', 'dropped'].map(function (s) {
        return '<option value="' + s + '"' + (t.status === s ? ' selected' : '') + '>' + s + '</option>';
      }).join('') + '</select></label>' +
      '</div></section>';

    var styleTip: Record<string, string> = {
      report: 'written as a structured bug report',
      simple: 'written as a short, casual report',
      session: 'written as the opening message to a coding agent',
    };
    var lead = 'This is the prompt the agent gets for this task (along with the pre-fix checkout — never the tests or the fix). ';
    var promptMeta = t.promptSource === 'real_issue'
      ? lead + 'Taken verbatim from the PR’s linked GitHub issue — written by a real user before the fix existed.'
      : t.promptSource === 'edited'
        ? lead + 'Edited by hand — the leak-check verdict no longer applies to this text.'
        : t.promptSource === 'generated'
          ? lead + 'Generated from the held-out tests' + (t.promptStyle ? ', ' + (styleTip[t.promptStyle] || t.promptStyle) : '') +
            ' — leak check: <b title="' + esc(CHECK_TIP[t.checkStatus] || '') + '">' + esc(t.checkStatus || 'pending') + '</b>' +
            (t.genAttempts > 1 ? ' after ' + t.genAttempts + ' drafts' : '') + '.'
          : 'No task prompt yet — run Generate task prompts on the benchmark.';
    html += '<section class="panel"><div class="panel-head"><h2>Task prompt</h2>' +
      (frozen ? '' : '<button class="btn" id="taskRegen" title="Discard this prompt and write a new leak-checked one">Regenerate</button>') +
      '<button class="btn" id="taskSave" style="display:none">Save</button></div>' +
      '<div class="bench-prompt-meta">' + promptMeta + '</div>' +
      '<textarea class="bench-prompt-input" id="taskPrompt" rows="7"' + dis + '>' + esc(t.problemStatement || '') + '</textarea>' +
      '</section>';

    var testList = function (title: string, tests: string[], note: string, key: string) {
      var h = '<section class="panel"><div class="panel-head"><h2>' + title + (tests.length ? ' (' + tests.length + ')' : '') + '</h2></div>';
      if (!tests.length) {
        return h + '<div class="empty">Pending validation — these tests are discovered when the task is execution-validated ' +
          '(checkout the fix, run the PR’s tests, rerun at the pre-fix commit; the flip defines them).</div></section>';
      }
      h += '<div class="bench-prompt-meta">' + note + '</div>';
      h += '<div class="bench-tests" id="tests-' + key + '">' + tests.slice(0, 12).map(function (x) { return '<div class="mono bench-test">' + esc(x) + '</div>'; }).join('') +
        (tests.length > 12 ? '<a class="bench-more" href="javascript:void(0)" data-more="' + key + '">… show ' + (tests.length - 12) + ' more</a>' : '') +
        '</div></section>';
      return h;
    };
    html += testList('Fail-to-pass tests', t.f2p, 'Held out from the agent; failing at the base commit, passing after the gold fix. All must pass to resolve the task.', 'f2p');
    html += testList('Pass-to-pass tests', t.p2p, 'Must stay green — regressions fail the task.', 'p2p');

    html += '<section class="panel"><div class="panel-head"><h2>Files</h2></div>' +
      '<div class="bench-tests">' +
      t.codeFiles.map(function (f: string) { return '<div class="mono bench-test">src&nbsp;·&nbsp;' + esc(f) + '</div>'; }).join('') +
      t.testFiles.map(function (f: string) { return '<div class="mono bench-test">test&nbsp;·&nbsp;' + esc(f) + '</div>'; }).join('') +
      '</div></section>';

    // The answer key — collapsed by default, clearly labeled as reviewer-only.
    if (t.goldDiff) {
      html += '<section class="panel"><div class="panel-head"><h2>Gold patch</h2>' +
        '<button class="btn" id="diffToggle">Show</button></div>' +
        '<div class="bench-prompt-meta">The actual fix (source files only' + (t.goldDiff.length >= 40000 ? ', truncated' : '') + '). ' +
        'This is the answer key: reviewers use it to judge the task and its problem statement — agents never see it.</div>' +
        '<div id="goldDiff" style="display:none">' + ghDiff(t.goldDiff) + '</div></section>';
    }

    root.innerHTML = html;
    wireNavLinks(root);

    var diffBtn = document.getElementById('diffToggle');
    if (diffBtn) diffBtn.onclick = function () {
      var d = document.getElementById('goldDiff');
      if (!d) return;
      var open = d.style.display !== 'none';
      d.style.display = open ? 'none' : '';
      diffBtn!.textContent = open ? 'Show' : 'Hide';
    };

    // "show N more" expanders for the test lists.
    Array.prototype.forEach.call(root.querySelectorAll('[data-more]'), function (a: HTMLElement) {
      a.onclick = function () {
        var key = a.getAttribute('data-more');
        var all = key === 'f2p' ? t.f2p : t.p2p;
        var box = document.getElementById('tests-' + key);
        if (box) box.innerHTML = all.map(function (x: string) { return '<div class="mono bench-test">' + esc(x) + '</div>'; }).join('');
      };
    });

    if (frozen) return; // no edit wiring on frozen benchmarks

    var promptEl = document.getElementById('taskPrompt') as HTMLTextAreaElement | null;
    var saveEl = document.getElementById('taskSave') as HTMLElement | null;
    if (promptEl) promptEl.oninput = function () { if (saveEl) saveEl.style.display = ''; };
    if (saveEl) saveEl.onclick = function () {
      patch('/api/bench/tasks/' + encodeURIComponent(id), { problemStatement: promptEl ? promptEl.value : '' })
        .then(function () { flashSaved(saveEl, function () { renderTask(root, id); }); });
    };
    var regenEl = document.getElementById('taskRegen') as HTMLButtonElement | null;
    if (regenEl) regenEl.onclick = function () {
      // Clear the statement, then kick the benchmark's generation job — it only
      // touches tasks with no statement, so this regenerates just this one
      // (plus any others still missing theirs). Stay HERE and poll until the
      // new prompt lands; navigating away would lose the reviewer's place.
      regenEl!.disabled = true;
      regenEl!.textContent = 'Regenerating…';
      var pel = document.getElementById('taskPrompt') as HTMLTextAreaElement | null;
      if (pel) { pel.value = ''; pel.placeholder = 'Writing a new leak-checked prompt…'; pel.disabled = true; }
      patch('/api/bench/tasks/' + encodeURIComponent(id), { problemStatement: '' })
        .then(function () { return post('/api/bench/benchmarks/' + encodeURIComponent(benchId) + '/generate-prompts'); })
        .then(function poll() {
          setTimeout(function () {
            if (state.view !== 'benchmarks' || state.bench.task !== id) return; // user left
            get('/api/bench/tasks/' + encodeURIComponent(id)).then(function (fresh) {
              if (fresh && fresh.problemStatement) renderTask(root, id);
              else poll();
            }).catch(poll);
          }, 4000);
        });
    };
    var diffEl = document.getElementById('taskDiff') as HTMLSelectElement | null;
    if (diffEl) diffEl.onchange = function () {
      patch('/api/bench/tasks/' + encodeURIComponent(id), { difficulty: diffEl!.value }).then(function () { renderTask(root, id); });
    };
    var stEl = document.getElementById('taskStatus') as HTMLSelectElement | null;
    if (stEl) stEl.onchange = function () {
      patch('/api/bench/tasks/' + encodeURIComponent(id), { status: stEl!.value }).then(function () { renderTask(root, id); });
    };
  }).catch(function () {
    root.innerHTML = '<div class="empty">Could not load this task — is the server reachable?</div>';
  });
}

// ---- run detail -------------------------------------------------------------

function renderRun(root: HTMLElement, id: string, background?: boolean): void {
  if (!background) root.innerHTML = '<div class="empty">Loading run…</div>';
  get('/api/bench/runs/' + encodeURIComponent(id)).then(function (r) {
    if (!r || r.error) { if (!background) root.innerHTML = '<div class="empty">Run not found.</div>'; return; }
    // Live runs repaint every 4s until they finish.
    if (r.status === 'queued' || r.status === 'running') {
      setTimeout(function () {
        if (state.view === 'benchmarks' && state.bench.run === id) renderRun(root, id, true);
      }, 4000);
    }
    var s = r.summary;
    var html = '';
    if (r.errorMsg) html += '<div class="notice bench-pipe">Run failed: ' + esc(r.errorMsg) + '</div>';

    html += '<section class="panel"><div class="bench-head">' +
      backArrow('Back to ' + (r.benchmarkName || 'benchmark'), { bench: r.benchmarkId }) +
      '<h2 class="bench-task-h">' + esc(r.harness) + ' / <b>' + esc(r.model) + '</b></h2>' +
      '<span class="badge ' + (r.status === 'done' ? 'b-success' : r.status === 'failed' ? 'b-failure' : 'b-partial') + '">' + esc(r.status) + '</span></div>' +
      '<div class="bench-meta">' +
      (r.params && r.params.attempts ? '<span class="kv"><b>Attempts</b> ' + esc(String(r.params.attempts)) + ' per task</span>' : '') +
      (r.params && r.params.temperature != null ? '<span class="kv"><b>Temperature</b> ' + esc(String(r.params.temperature)) + '</span>' : '') +
      (r.startedAt ? '<span class="kv"><b>Started</b> ' + esc(dayOf(r.startedAt)) + '</span>' : '') +
      '</div>' +
      (s && r.attempts.length ? '<section class="tiles bench-tiles">' +
        '<div class="tile"><div class="metric-head">Resolve rate' + (s.attemptsPerTask > 1 ? ' · pass@' + s.attemptsPerTask : '') +
          ((r.status === 'running' || r.status === 'queued') ? ' <span class="badge b-partial">so far</span>' : '') + '</div>' +
          '<div class="metric-big">' + pct(s.resolveRate) + '</div>' +
          '<div class="metric-sub">' + num(s.solved) + ' of ' + plural(s.tasks, 'task') +
          ((r.status === 'running' || r.status === 'queued') ? ' · ' + num(s.attempted) + ' attempted so far' : '') + '</div></div>' +
        (s.attemptsPerTask > 1
          ? '<div class="tile"><div class="metric-head">Resolve rate · pass@1</div><div class="metric-big">' + pct(s.resolveRateFirstTry) + '</div><div class="metric-sub">first attempt only</div></div>'
          : '') +
        '<div class="tile"><div class="metric-head">Cost / task</div><div class="metric-big">' + (s.costPerTask != null ? usd(s.costPerTask) : '—') + '</div><div class="metric-sub">mean, all attempts</div></div>' +
        '<div class="tile"><div class="metric-head">Tokens / task</div><div class="metric-big">' + (s.tokensPerTask != null ? num(Math.round(s.tokensPerTask / 1000)) + 'k' : '—') + '</div><div class="metric-sub">mean, all attempts</div></div>' +
        '</section>' : '') +
      '</section>';

    var expected = r.params && r.params.totalAttempts ? Number(r.params.totalAttempts) : 0;
    html += '<section class="panel"><div class="panel-head"><h2>Attempts' +
      (expected && (r.status === 'queued' || r.status === 'running') ? ' (' + r.attempts.length + ' / ' + expected + ')' : '') + '</h2></div>';
    if (!r.attempts.length) {
      html += (r.status === 'queued' || r.status === 'running'
        ? '<div class="empty"><span class="bench-spin"></span>Executing — each task gets a fresh container from the environment image: ' +
          'checkout the pre-fix commit, strip git history, hand the harness the task prompt, apply its patch in a clean container, ' +
          'overlay the held-out tests, and grade by running them. An attempt is a full agent session plus a graded test run, ' +
          'so the first rows typically land 10–30 minutes after the run starts.</div>'
        : '<div class="empty">No attempts recorded for this run.</div>') +
        '</section>';
      var y0 = window.scrollY;
      root.innerHTML = html;
      window.scrollTo(0, y0);
      wireNavLinks(root);
      return;
    }
    html += '<div class="bench-tbl-scroll"><table class="bench-tbl"><thead><tr><th>PR</th><th>Task</th><th>Difficulty</th><th>Verdict</th>' +
      '<th>F2P</th><th>P2P</th><th>Cost</th>' +
      '<th title="Wall-clock time for this attempt — the agent solving plus the grading run.">Time</th></tr></thead><tbody>';
    r.attempts.forEach(function (a: any, i: number) {
      html += '<tr class="bench-row" data-tx="' + i + '">' +
        '<td class="mono">#' + esc(a.prNumber != null ? String(a.prNumber) : '') + '</td>' +
        '<td class="bench-task-title">' + esc(a.prTitle || '') + '</td>' +
        '<td>' + diffBadge(a.difficulty) + '</td>' +
        '<td>' + verdictBadge(a.verdict) + '</td>' +
        '<td class="num">' + (a.f2pTotal != null ? a.f2pPass + ' / ' + a.f2pTotal : '—') + '</td>' +
        '<td class="num">' + (a.p2pTotal != null ? a.p2pPass + ' / ' + a.p2pTotal : '—') + '</td>' +
        '<td class="num">' + (a.costUsd != null ? usd(a.costUsd) : '—') + '</td>' +
        '<td class="num" title="' + (a.durationSecs != null ? Math.round(a.durationSecs) + ' seconds' : '') + '">' + fmtDuration(a.durationSecs) + '</td></tr>' +
        '<tr class="bench-tx-row" id="tx-' + i + '" style="display:none"><td colspan="8">' +
        '<div class="bench-prompt-meta">Transcript excerpt — grading is execution-only (F2P/P2P), not from what the agent claims.</div>' +
        '<pre class="bench-tx">' + esc(a.transcript || '(no transcript captured)') + '</pre></td></tr>';
    });
    html += '</tbody></table></div>' +
      '<div class="empty" style="margin-top:10px">Grading is execution-only: the agent’s patch is applied at the base commit, the canonical tests are re-overlaid from the gold commit, and the suite runs in the environment image. No LLM judge.</div></section>';

    var y = window.scrollY;
    root.innerHTML = html;
    window.scrollTo(0, y);
    wireNavLinks(root);
    Array.prototype.forEach.call(root.querySelectorAll('[data-tx]'), function (row: HTMLElement) {
      row.onclick = function () {
        var tx = document.getElementById('tx-' + row.getAttribute('data-tx'));
        if (tx) tx.style.display = tx.style.display === 'none' ? '' : 'none';
      };
    });
  }).catch(function () {
    // Same invariant renderBenchmark's catch implements: a BACKGROUND repaint
    // failure must never blank the page or stop the poll — one hiccup during
    // the 4s live-run cycle previously replaced the attempt matrix with this
    // error and never rescheduled, so the page could not recover even after
    // connectivity returned (review finding). Keep the stale paint, retry.
    if (background) {
      setTimeout(function () {
        if (state.view === 'benchmarks' && state.bench.run === id) renderRun(root, id, true);
      }, 4000);
      return;
    }
    root.innerHTML = '<div class="empty">Could not load this run — is the server reachable? ' +
      '<a href="javascript:void(0)" id="runRetry">Retry</a></div>';
    var r = document.getElementById('runRetry');
    if (r) r.onclick = function () { renderRun(root, id, false); };
  });
}
